<template>
  <BaseModal :title="'Cast options'" @close="closeModal">
    <template #modalContent>
      <div class="com">
        <header class="com-head">
          <p class="com-eyebrow">Cast options</p>
          <h2 class="com-title">What would you like to do?</h2>
        </header>

        <section class="com-grid">
          <button
            v-for="opt in visibleOptions"
            :key="opt.key"
            type="button"
            class="com-tile"
            :class="{ danger: opt.danger }"
            @click="onSelect(opt)"
          >
            <span class="com-tile-icon" v-html="opt.icon"></span>
            <span class="com-tile-body">
              <span class="com-tile-title">{{ opt.title }}</span>
              <span class="com-tile-desc">{{ opt.desc }}</span>
            </span>
          </button>
        </section>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from '@/components/common/BaseModal.vue';

// Inline SVGs (kept small and consistent). Stroke color is
// inherited via currentColor so the .danger tile can flip them red.
const I = {
  edit: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>',
  calendar:
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>',
  brush:
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M7 19a4 4 0 1 1-4-4"/><path d="M21 3l-9 9"/><path d="M14 4l6 6"/></svg>',
  sliders:
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="6" x2="11" y2="6"/><line x1="14" y1="6" x2="20" y2="6"/><circle cx="12.5" cy="6" r="2"/><line x1="4" y1="12" x2="6" y2="12"/><line x1="9" y1="12" x2="20" y2="12"/><circle cx="7.5" cy="12" r="2"/><line x1="4" y1="18" x2="14" y2="18"/><line x1="17" y1="18" x2="20" y2="18"/><circle cx="15.5" cy="18" r="2"/></svg>',
  link: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
  users:
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
  cast: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 16.1A5 5 0 0 1 5.9 20"/><path d="M2 12.05A9 9 0 0 1 9.95 20"/><path d="M2 8V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6"/></svg>',
  trash:
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>',
};

export default {
  name: 'CastOptionsModal',
  props: ['closeModal', 'setActiveModal', 'castDetails'],
  components: { BaseModal },
  data() {
    return {
      inviteeList: [],
      // Feature flag: hides NFT / NFT-gating / Credential-gating
      // tiles while those flows are paused. Flip to true to restore.
      showAdvancedAccess: false,
    };
  },
  computed: {
    canCustomUrl() {
      return (
        this.castDetails &&
        this.castDetails.cast_type === 'public' &&
        !this.castDetails.isNftGated
      );
    },
    hasInvitees() {
      return (this.inviteeList || []).length > 0;
    },
    isPostpone() {
      return this.hasInvitees;
    },
    options() {
      const list = [
        // ---- Advanced access (hidden via showAdvancedAccess flag) ----
        {
          key: 'nftDrop',
          hidden: !this.showAdvancedAccess,
          icon: I.cast,
          title:
            this.castDetails && this.castDetails.nft_details_submitted
              ? 'Edit NFT / Token drop'
              : 'NFT / Token drop',
          desc: 'Reward attendees with an on-chain drop.',
          action:
            this.castDetails && this.castDetails.nft_details_submitted
              ? 'editNftDropModal'
              : 'nftDropModal',
        },
        {
          key: 'nftGating',
          hidden: !this.showAdvancedAccess,
          icon: I.cast,
          title:
            this.castDetails && this.castDetails.isNftGated
              ? 'Edit NFT gating'
              : 'NFT gating',
          desc: 'Restrict joining to NFT holders.',
          action: 'createNFTGating',
        },
        {
          key: 'credGating',
          hidden: !this.showAdvancedAccess,
          icon: I.cast,
          title:
            this.castDetails &&
            (this.castDetails.vc_gated || this.castDetails.vc_details_submitted)
              ? 'Manage credentials'
              : 'Credential gating',
          desc: 'Verify attendees by credential.',
          action:
            this.castDetails &&
            (this.castDetails.vc_gated || this.castDetails.vc_details_submitted)
              ? 'credentialManagement'
              : 'credentialGating',
        },

        // ---- Always visible ----
        {
          key: 'customUrl',
          hidden: !this.canCustomUrl,
          icon: I.link,
          title: 'Custom cast URL',
          desc: 'Choose a memorable shortlink.',
          action: 'customCastUrl',
        },
        {
          key: 'audience',
          icon: I.users,
          title: 'Manage audience',
          desc: 'Add, remove or re-invite people.',
          action: 'manageAudienceModal',
        },
        {
          key: 'stream',
          icon: I.cast,
          title: 'Live streaming',
          desc: 'Restream to YouTube, Twitch, Facebook.',
          action: 'liveStreamModal',
        },

        // ---- Edit cluster ----
        {
          key: 'setup',
          icon: I.calendar,
          title: this.isPostpone ? 'Postpone cast' : 'Edit setup',
          desc: this.isPostpone
            ? 'Move this cast to a new date or time.'
            : 'Change schedule, timezone and duration.',
          action: this.isPostpone ? 'editPostponeDetail' : 'editSetupDetail',
        },
        {
          key: 'basic',
          icon: I.edit,
          title: 'Edit basic details',
          desc: 'Name, description and access type.',
          action: 'editBasicDetail',
        },
        {
          key: 'branding',
          icon: I.brush,
          title: 'Edit branding',
          desc: 'Logo, cover image and accent color.',
          action: 'editBrandingDetail',
        },
        {
          key: 'advanced',
          icon: I.sliders,
          title: 'Edit advanced',
          desc: 'Recording, mute, permissions.',
          action: 'editAdvanceDetail',
        },

        // ---- Danger ----
        {
          key: 'delete',
          icon: I.trash,
          title: 'Delete cast',
          desc: 'Remove this cast permanently.',
          action: 'deleteCastModal',
          danger: true,
        },
      ];
      return list;
    },
    visibleOptions() {
      return this.options.filter((o) => !o.hidden);
    },
  },
  methods: {
    onSelect(opt) {
      if (!opt || !opt.action) return;
      this.setActiveModal && this.setActiveModal(opt.action);
    },
    userDetails() {
      const t = document.getElementById('loading-bg-transparent-title');
      const bg = document.getElementById('loading-bg-transparent');
      if (t) t.innerText = 'Loading cast options…';
      if (bg) bg.style.display = 'flex';
      const payload = {
        cast_id: this.castDetails && this.castDetails.public_meeting_id,
      };
      this.$store
        .dispatch('studio/inviteeDetails', payload)
        .then(async (res) => {
          this.inviteeList = (await res.data.data) || [];
          if (bg) bg.style.display = 'none';
        })
        .catch(() => {
          if (bg) bg.style.display = 'none';
        });
    },
  },
  mounted() {
    this.userDetails();
  },
};
</script>

<style scoped>
.com {
  width: 640px;
  max-width: calc(100vw - 32px);
  padding: 10px 6px 4px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  color: #fff;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}

.com-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.com-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}
.com-title {
  font-size: 22px;
  font-weight: 600;
  color: #fff;
  margin: 0;
  letter-spacing: -0.005em;
}

.com-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.com-tile {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;
  min-height: 78px;
}
.com-tile:hover {
  background: rgba(215, 223, 35, 0.05);
  border-color: rgba(215, 223, 35, 0.4);
}
.com-tile-icon {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.8);
  flex-shrink: 0;
}
.com-tile:hover .com-tile-icon {
  color: #d7df23;
  background: rgba(215, 223, 35, 0.1);
}
.com-tile-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.com-tile-title {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.005em;
}
.com-tile-desc {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.4;
}

.com-tile.danger {
  background: rgba(239, 68, 68, 0.04);
  border-color: rgba(239, 68, 68, 0.3);
  grid-column: 1 / -1;
}
.com-tile.danger .com-tile-icon {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}
.com-tile.danger .com-tile-title {
  color: #ef4444;
}
.com-tile.danger:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.55);
}
.com-tile.danger:hover .com-tile-icon {
  background: rgba(239, 68, 68, 0.18);
  color: #ef4444;
}

@media (max-width: 640px) {
  .com {
    width: 100%;
  }
  .com-grid {
    grid-template-columns: 1fr;
  }
}
</style>
